import React, { useEffect, useState, useMemo } from 'react';
import { FaEye, FaEdit, FaBan, FaDownload, FaPlus, FaSort, FaSortUp, FaSortDown, FaCheck, FaTrash, FaSearch } from 'react-icons/fa';
import dayjs from 'dayjs';
import SubcategoryFormModal from './SubcategoryFormModal';
import SubcategoryViewModal from './SubcategoryViewModal';
import Sidebar from './Sidebar';
import { useScreenPermissions } from '../components/useScreenPermissions';

import { useNavigate } from 'react-router-dom';

const LeftArrowIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path fill="currentColor" d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 246.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"/></svg>;
const RightArrowIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path fill="currentColor" d="M278.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-160 160c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L210.7 256 73.4 118.6c12.5-12.5 12.5-32.8 0-45.3s32.8-12.5-45.3 0l160 160z"/></svg>;
const API_URL = '/api';

const SubcategoryMaster = () => {
  const [subcategories, setSubcategories] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('All');
  const [category, setCategory] = useState('All');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();
  
  // States for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  
  // Helper function to normalize status
  const normalizeStatus = (status) => {
    if (status === true || status === 1 || status === '1' || 
        String(status).toLowerCase() === 'active') {
      return 'Active';
    } else {
      return 'Inactive';
    }
  };
  
  // Helper function to get status color
  const getStatusColor = (status) => {
    const isActive = status === true || status === 1 || status === '1' || 
                   String(status).toLowerCase() === 'active';
    return isActive ? 'bg-[#ecfdf5] text-[#064e3b]' : 'bg-[#fff7ed] text-[#92400e]';
  };
  
  useEffect(() => {
    fetchSubcategories();
  }, []);
  
  // Add scroll listener for sticky header behavior
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const fetchSubcategories = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/subcategories`);
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      const data = await res.json();
      console.log('Subcategories API response:', data);
      // Ensure data is an array
      setSubcategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      setSubcategories([]);
    }
    setLoading(false);
  };

  const handleLogout = () => {
    navigate('/login');
  };
  
  const handleSort = (key) => {
    if (!Array.isArray(subcategories)) return;
    
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    const sortedSubcategories = [...subcategories].sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;
      
      // Handle date fields
      if (key === 'CreatedDate') {
        return direction === 'asc' 
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      }
      
      // Handle numeric fields
      if (key === 'SubcategoryCode') {
        const aNum = parseInt(a[key].split('-')[1]);
        const bNum = parseInt(b[key].split('-')[1]);
        return direction === 'asc' ? aNum - bNum : bNum - aNum;
      }
      // Handle string fields
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setSubcategories(sortedSubcategories);
  };
  
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ml-1 w-4 h-4 text-gray-400" />;
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="ml-1 w-4 h-4 text-blue-600" /> 
      : <FaSortDown className="ml-1 w-4 h-4 text-blue-600" />;
  };
  
  const handleView = (subcategory) => {
    setSelectedSubcategory(subcategory);
    setShowView(true);
  };
  
  const handleEdit = (subcategory) => {
    setSelectedSubcategory(subcategory);
    setIsEdit(true);
    setShowForm(true);
  };
  
  const handleDelete = async (subcategory) => {
    if (!window.confirm('Are you sure you want to delete this subcategory?')) return;
    
    try {
      const response = await fetch(`${API_URL}/subcategories/${subcategory.SubcategoryCode}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete subcategory');
      }
      
      fetchSubcategories();
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      alert(error.message || 'An error occurred while deleting the subcategory');
    }
  };
  
  const handleDownloadCSV = () => {
    if (!Array.isArray(subcategories) || subcategories.length === 0) {
      alert('No subcategories to export');
      return;
    }
    
    const headers = [
      'Subcategory Code', 'Subcategory Name', 'Parent Category', 'Category Code', 'Description', 'Status', 'Created Date'
    ];
    const rows = subcategories.map(s => [
      s.SubcategoryCode, s.SubcategoryName, s.ParentCategoryName || '-', s.ParentCategoryCode || '-', 
      s.Description || '-', normalizeStatus(s.Status), dayjs(s.CreatedDate).format('YYYY-MM-DD HH:mm')
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'subcategories.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };
  
  const handleCreate = () => {
    setSelectedSubcategory(null);
    setIsEdit(false);
    setShowForm(true);
  };
  
  const handleFormSubmit = async (formData) => {
    setFormLoading(true);
    try {
      if (isEdit && selectedSubcategory) {
        const response = await fetch(`${API_URL}/subcategories/${selectedSubcategory.SubcategoryCode}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update subcategory');
        }
      } else {
        const response = await fetch(`${API_URL}/subcategories`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create subcategory');
        }
      }
      setShowForm(false);
      fetchSubcategories();
    } catch (error) {
      console.error('Error saving subcategory:', error);
      alert(error.message || 'An error occurred while saving the subcategory');
    } finally {
      setFormLoading(false);
    }
  };
  
  // Filter subcategories based on search term and filters
  const filteredSubcategories = subcategories.filter(subcategory => {
    const matchesSearch = searchTerm === '' ||
      (subcategory.SubcategoryCode && subcategory.SubcategoryCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (subcategory.SubcategoryName && subcategory.SubcategoryName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (subcategory.Description && subcategory.Description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (subcategory.ParentCategoryName && subcategory.ParentCategoryName.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const normalizedStatus = normalizeStatus(subcategory.Status);
    const matchesStatus = status === 'All' || normalizedStatus === status;
    const matchesCategory = category === 'All' || subcategory.ParentCategoryName === category;
    return matchesSearch && matchesStatus && matchesCategory;
  });
  
  // Pagination logic
  const totalPages = Math.ceil(filteredSubcategories.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const subcategoriesForPage = filteredSubcategories.slice(startIndex, endIndex);
  
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  
  const renderPaginationButtons = () => {
    const pages = [];
    // Show a max of 5 page buttons
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);
    
    // Render "previous" button
    pages.push(
      <button
        key="prev"
        className="p-2 rounded-none transition-colors duration-150"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="Previous page"
        style={{ color: currentPage === 1 ? '#c7b1aa' : '#3e2e26' }}
      >
        <LeftArrowIcon className="w-4 h-4" />
      </button>
    );
    
    // Render page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`px-3 py-1 rounded-none transition-colors duration-200 focus:outline-none focus-visible:ring-2 ${
            i === currentPage ? 'bg-[#ffc9b8] text-black font-semibold' : 'text-[#3e2e26] hover:bg-[#fff0e9]'
          }`}
          onClick={() => handlePageChange(i)}
          aria-current={i === currentPage ? 'page' : undefined}
        >
          {i}
        </button>
      );
    }
    
    // Render "next" button
    pages.push(
      <button
        key="next"
        className="p-2 rounded-none transition-colors duration-150"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages || totalPages === 0}
        aria-label="Next page"
        style={{ color: (currentPage === totalPages || totalPages === 0) ? '#c7b1aa' : '#3e2e26' }}
      >
        <RightArrowIcon className="w-4 h-4" />
      </button>
    );
    
    return pages;
  };
  
  // Get unique parent categories for filter dropdown
  const parentCategories = [...new Set(subcategories.map(s => s.ParentCategoryName).filter(Boolean))];
  
  // Calculate stats with proper status normalization
  const totalSubcategories = subcategories.length;
  const activeSubcategories = subcategories.filter(s => {
    const normalizedStatus = normalizeStatus(s.Status);
    return normalizedStatus === 'Active';
  }).length;

  // Check permissions for actions
  const canCreate = hasFullAccess || hasPermission('MRN_MASTER', 'CanCreate');
  const canView = hasFullAccess || hasPermission('MRN_MASTER', 'CanView');
  const canEdit = hasFullAccess || hasPermission('MRN_MASTER', 'CanEdit');
  const canDelete = hasFullAccess || hasPermission('MRN_MASTER', 'CanDelete');
 
  // THEME OBJECT (used to build CSS variables and to pass to Sidebar)
  const pageTheme = useMemo(() => ({
    bg: "#fff7f2",
    sidebarBg: "#fff0e9",
    accent: "#ffc9b8",
    border: "#f3d9cf",
    text: "#3e2e26",
    muted: "#63514a",
    iconColor: "#4a352d",
    hoverBg: "#fff7f2",
  }), []);
  
  // Setup CSS variables and small scoped stylesheet so Sidebar and page match (similar to VendorMaster)
  useEffect(() => {
    const themeVars = {
      "--sidebar-line-color": pageTheme.border,
      "--sidebar-bg": pageTheme.sidebarBg,
      "--sidebar-text": pageTheme.text,
      "--accent-color": pageTheme.accent,
      "--accent-hover": pageTheme.accent,
      "--page-border-color": pageTheme.border,
      "--page-bg": pageTheme.bg,
      "--text-primary": pageTheme.text,
      "--text-muted": pageTheme.muted,
      "--icon-color": pageTheme.iconColor,
      "--hover-bg": pageTheme.hoverBg
    };

    Object.entries(themeVars).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });

    const styleId = 'subcategory-theme-overrides';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        .primary-btn { background-color: var(--accent-color); color: #ffffff; border-radius: 0; }
        .primary-btn:hover { background-color: var(--accent-hover); }

        .secondary-btn { background-color: #ffffff; color: var(--text-primary); border: 1px solid var(--page-border-color); border-radius: 0; }
        .secondary-btn:hover { background-color: var(--hover-bg); }

        .status-active { background-color: #ecfdf5; color: #064e3b; border-radius: 6px; padding: 4px 10px; font-size: 12px; display: inline-block; font-weight: 600; }
        .status-inactive { background-color: #fff7ed; color: #92400e; border-radius: 6px; padding: 4px 10px; font-size: 12px; display: inline-block; font-weight: 600; }

        .accent-icon { color: var(--accent-color); }
        .accent-icon-hover:hover { color: var(--accent-hover); }

        .page-active { background-color: var(--accent-color); color: #fff; border-radius: 0; }
        .page-inactive { color: var(--accent-color); }
        .page-inactive:hover { background-color: var(--hover-bg); }

        .sidebar, .app-sidebar { background-color: var(--sidebar-bg) !important; color: var(--sidebar-text) !important; }
        .sidebar a, .sidebar .menu-item, .app-sidebar a, .app-sidebar .menu-item { color: var(--sidebar-text) !important; }

        /* stronger active/inactive arrow colors in tables */
        .table-header-sort .sort-inactive { opacity: 0.85; color: #9aa7b0; }
        .table-header-sort .sort-active { opacity: 1; color: #155e52; }
      `;
      document.head.appendChild(style);
    }

    // cleanup is intentionally minimal: variables remain so other components keep consistent theme
    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, [pageTheme]);

  // sidebarTheme object passed to Sidebar so it matches page theme (uses CSS vars where possible)
  const sidebarTheme = useMemo(() => ({
    text: 'var(--sidebar-text, #3e2e26)',
    muted: 'var(--text-muted, #63514a)',
    border: 'var(--page-border-color, #f3d9cf)',
    sidebarBorder: 'var(--sidebar-line-color, #f3d9cf)',
    background: 'var(--sidebar-bg, #fff0e9)',
    pageBackground: 'var(--page-bg, #fff7f2)',
    activeBg: 'var(--accent-color, #ffc9b8)',
    profileBg: 'var(--accent-color, #ffc9b8)',
    activeText: '#100d0dff',
    accent: 'var(--accent-color, #ffc9b8)',
    accentHover: 'var(--accent-hover, #ffc9b8)',
    hoverGray: 'rgba(0,0,0,0.04)',
    iconColor: 'var(--icon-color, #4a352d)',
    sidebarBg: 'var(--sidebar-bg, #fff0e9)'
  }), []);

  if (permissionsLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center" style={{ backgroundColor: pageTheme.bg }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 mx-auto" style={{ borderColor: pageTheme.accent, borderBottomColor: '#e6c8bd' }}></div>
          <p className="mt-4" style={{ color: pageTheme.muted }}>Loading permissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex"
      style={{
        backgroundColor: pageTheme.bg,
        '--radius-md': '8px',
        '--card-shadow': '0 6px 16px rgba(62,46,38,0.06)',
        '--divider-color': pageTheme.border,
        '--page-accent': pageTheme.accent,
        '--sidebar-bg': pageTheme.sidebarBg,
        '--sidebar-text': pageTheme.text,
        '--hover-bg': pageTheme.hoverBg
      }}
    >
      <Sidebar 
        collapsed={sidebarCollapsed}
        onToggle={setSidebarCollapsed}
        theme={sidebarTheme}
        onLogout={handleLogout}
      />
      <main className={`flex-1 p-8 overflow-hidden ml-20 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}
`}>
        
        {/* Header Section */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold" style={{ color: pageTheme.text }}>Subcategory Master</h1>
              <p style={{ color: pageTheme.muted }}>Manage your product subcategories and their parent categories</p>
              {/* soft divider under header - slightly thicker */}
              <div className="w-full" style={{ borderBottom: `2px solid ${pageTheme.border}`, marginTop: 12, marginBottom: 12 }} />
            </div>
            <div className="flex items-center gap-3">
              <button
                className="px-4 py-2 rounded-none border text-[#3e2e26] hover:opacity-95 transition-all duration-300 flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                onClick={handleDownloadCSV}
                style={{ borderColor: pageTheme.border, backgroundColor: pageTheme.sidebarBg }}
                aria-label="Export subcategories CSV"
              >
                <FaDownload className="w-4 h-4" />
                Export
              </button>
              {canCreate && (
                <button
                  onClick={handleCreate}
                  className="text-[#3e2e26] font-semibold px-4 py-2 rounded-none shadow transition-all duration-300 flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  style={{ backgroundColor: pageTheme.accent, borderColor: pageTheme.border, boxShadow: 'var(--card-shadow)' }}
                  aria-label="Create new subcategory"
                >
                  <FaPlus className="w-4 h-4" />
                  Create New
                </button>
              )}
            </div>
          </div>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-5 rounded-none border shadow-md" style={{ borderColor: pageTheme.border, boxShadow: 'var(--card-shadow)' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: pageTheme.muted }}>Total Subcategories</p>
                <p className="text-2xl font-bold" style={{ color: pageTheme.text }}>{totalSubcategories}</p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-none" style={{ backgroundColor: pageTheme.sidebarBg, color: pageTheme.iconColor }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
            </div>
          </div>
          <div className="bg-white p-5 rounded-none border shadow-md" style={{ borderColor: pageTheme.border, boxShadow: 'var(--card-shadow)' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: pageTheme.muted }}>Active Subcategories</p>
                <p className="text-2xl font-bold" style={{ color: pageTheme.text }}>{activeSubcategories}</p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-none bg-green-50 text-green-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="bg-white p-5 rounded-none border shadow-md" style={{ borderColor: pageTheme.border, boxShadow: 'var(--card-shadow)' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: pageTheme.muted }}>Parent Categories</p>
                <p className="text-2xl font-bold" style={{ color: pageTheme.text }}>
                  {[...new Set(subcategories.map(s => s.ParentCategoryName).filter(Boolean))].length}
                </p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-none bg-yellow-50 text-yellow-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="bg-white p-5 rounded-none border shadow-md" style={{ borderColor: pageTheme.border, boxShadow: 'var(--card-shadow)' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: pageTheme.muted }}>This Month</p>
                <p className="text-2xl font-bold" style={{ color: pageTheme.text }}>
                  {subcategories.filter(s => {
                    const createdDate = new Date(s.CreatedDate);
                    const now = new Date();
                    return createdDate.getMonth() === now.getMonth() &&
                           createdDate.getFullYear() === now.getFullYear();
                  }).length}
                </p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-none bg-purple-50 text-purple-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        {/* Search and Filters */}
        <div className="bg-white p-5 mb-6 border rounded-none" style={{ borderColor: pageTheme.border }}>
          <div className="flex items-end space-x-4">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2" style={{ color: pageTheme.muted }}>What are you looking for?</label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: '#8b7a73' }} />
                <input
                  type="text"
                  placeholder="Search for subcategory code, name, description, etc"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-none focus:outline-none focus:ring-2 transition-all duration-200"
                  style={{ borderColor: pageTheme.border, boxShadow: '0 0 0 3px rgba(255,201,184,0.08)' }}
                  aria-label="Search subcategories"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: pageTheme.muted }}>Parent Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-48 py-2 px-3 border rounded-none focus:outline-none focus:ring-2 transition-all duration-200"
                style={{ borderColor: pageTheme.border, boxShadow: '0 0 0 3px rgba(255,201,184,0.08)' }}
              >
                <option>All</option>
                {parentCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: pageTheme.muted }}>Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-48 py-2 px-3 border rounded-none focus:outline-none focus:ring-2 transition-all duration-200"
                style={{ borderColor: pageTheme.border, boxShadow: '0 0 0 3px rgba(255,201,184,0.08)' }}
              >
                <option>All</option>
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </div>
            <button
              className="text-[#3e2e26] font-semibold px-6 py-2 rounded-none focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              onClick={() => setCurrentPage(1)}
              style={{ backgroundColor: pageTheme.accent }}
              aria-label="Search"
            >
              Search
            </button>
          </div>
        </div>
        
        {/* Subcategories Table */}
        <section className="bg-white border" style={{ borderColor: pageTheme.border }}>
          <div className="flex justify-between items-center p-4 border-b" style={{ borderColor: pageTheme.border }}>
            <h2 className="text-xl font-semibold" style={{ color: pageTheme.text }}>Subcategory Summary</h2>
            <div className="flex gap-2">{renderPaginationButtons()}</div>
          </div>

          {loading ? (
            <div className="p-8 text-center" style={{ color: pageTheme.muted }}>Loading subcategories...</div>
          ) : subcategoriesForPage.length === 0 ? (
            <div className="p-8 text-center" style={{ color: pageTheme.muted }}>No subcategories found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs font-semibold uppercase" style={{ backgroundColor: '#ffefe6', color: pageTheme.muted }}>
                  <tr>
                    {[
                      { key: 'SubcategoryCode', label: 'Subcategory Code' },
                      { key: 'SubcategoryName', label: 'Subcategory Name' },
                      { key: 'ParentCategoryName', label: 'Parent Category' },
                      { key: 'ParentCategoryCode', label: 'Category Code' },
                      { key: 'Description', label: 'Description' },
                      { key: 'Status', label: 'Status' },
                      { key: 'CreatedDate', label: 'Created_at' },
                      { key: 'actions', label: 'Actions' },
                    ].map((col) => (
                      <th
                        key={col.key}
                        className="px-6 py-3 text-left cursor-pointer transition-colors duration-200"
                        onClick={() => col.key !== 'actions' && handleSort(col.key)}
                        style={{ borderBottom: `1px solid ${pageTheme.border}` }}
                      >
                        <div className="flex items-center">
                          {col.label}
                          {col.key !== 'actions' && (
                            <span className="ml-2 flex flex-col">
                              <FaSortUp className={`h-3 w-3 ${sortConfig.key === col.key && sortConfig.direction === 'asc' ? 'text-blue-600' : 'text-gray-500'}`} />
                              <FaSortDown className={`h-3 w-3 -mt-1.5 ${sortConfig.key === col.key && sortConfig.direction === 'desc' ? 'text-blue-600' : 'text-gray-500'}`} />
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: pageTheme.border }}>
                  {subcategoriesForPage.map((subcategory, idx) => (
                    <tr key={subcategory.SubcategoryCode || idx} className="transition-colors duration-200" style={{ backgroundColor: '#ffffff' }}>
                      <td className="px-6 py-3 font-medium" style={{ color: pageTheme.text }}>{subcategory.SubcategoryCode || '-'}</td>
                      <td className="px-6 py-3" style={{ color: pageTheme.muted }}>{subcategory.SubcategoryName || '-'}</td>
                      <td className="px-6 py-3" style={{ color: pageTheme.muted }}>{subcategory.ParentCategoryName || '-'}</td>
                      <td className="px-6 py-3" style={{ color: pageTheme.muted }}>{subcategory.ParentCategoryCode || '-'}</td>
                      <td className="px-6 py-3 break-all max-w-[200px]" style={{ color: pageTheme.muted }}>{subcategory.Description || '-'}</td>
                      <td className="px-6 py-3">
                        <span
                          className={`inline-block px-3 py-1 text-xs font-medium cursor-pointer rounded-full transition-colors duration-200`}
                          style={{
                            backgroundColor: (normalizeStatus(subcategory.Status) === 'Active') ? '#ecfdf5' : '#fff7ed',
                            color: (normalizeStatus(subcategory.Status) === 'Active') ? '#064e3b' : '#92400e'
                          }}
                        >
                          {normalizeStatus(subcategory.Status)}
                        </span>
                      </td>
                      <td className="px-6 py-3" style={{ color: pageTheme.muted }}>{subcategory.CreatedDate ? dayjs(subcategory.CreatedDate).format('DD MMM YYYY, HH:mm') : '-'}</td>
                      <td className="px-6 py-3">
                        <div className="flex gap-3 justify-center">
                          {canView && (
                            <button onClick={() => handleView(subcategory)} title="View Details" className="transition-colors duration-200" style={{ color: pageTheme.accent }}>
                              <FaEye className="h-4 w-4" />
                            </button>
                          )}
                          {canEdit && (
                            <button onClick={() => handleEdit(subcategory)} title="Edit" className="transition-colors duration-200" style={{ color: '#16a34a' }}>
                              <FaEdit className="h-4 w-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button onClick={() => handleDelete(subcategory)} title="Delete" className="transition-colors duration-200" style={{ color: '#dc2626' }}>
                              <FaTrash className="h-4 w-4" />
                            </button>
                          )}
                          <button onClick={handleDownloadCSV} title="Download CSV" className="transition-colors duration-200" style={{ color: '#7c3aed' }}>
                            <FaDownload className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        
        {/* Modals */}
        <SubcategoryFormModal
          open={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={handleFormSubmit}
          initialData={selectedSubcategory}
          isEdit={isEdit}
          loading={formLoading}
        />
        <SubcategoryViewModal
          open={showView}
          onClose={() => setShowView(false)}
          subcategory={selectedSubcategory}
        />
      </main>
    </div>
  );
};
export default SubcategoryMaster;
